import type { GtmBrainDoc } from "../api/GtmApi";

export type CompetitorListItem = {
  key: string;
  index: number;
  doc: GtmBrainDoc;
  label: string;
  filled: number;
  total: number;
  isNew: boolean;
};

export type CompetitorFieldId = "name" | "domain" | "linkedinUrl" | "description" | "notes";

function hasText(value?: string | null): boolean {
  return !!value && value.trim().length > 0;
}

export function competitorFill(doc: GtmBrainDoc): { filled: number; total: number } {
  const checks = [
    hasText(doc.name),
    hasText(doc.domain),
    hasText(doc.linkedinUrl),
    hasText(doc.description),
    hasText(doc.notes),
  ];
  return { filled: checks.filter(Boolean).length, total: checks.length };
}

export function competitorLabel(doc: GtmBrainDoc, _index: number): string {
  const name = (doc.name || "").trim();
  if (name) return name.length > 72 ? `${name.slice(0, 69)}…` : name;
  return "New reference";
}

/** Stable key — keep in sync with brainToFlow docLeaves ids. */
export function competitorKey(doc: GtmBrainDoc, competitorIndex: number): string {
  return doc.id || `doc-competitor-${competitorIndex}`;
}

export function listCompetitors(docs: GtmBrainDoc[]): CompetitorListItem[] {
  const only = docs
    .map((doc, index) => ({ doc, index }))
    .filter(({ doc }) => doc.docType === "competitor");
  return only.map(({ doc, index }, competitorIndex) => {
    const fill = competitorFill(doc);
    const label = competitorLabel(doc, competitorIndex);
    return {
      key: competitorKey(doc, competitorIndex),
      index,
      doc,
      label,
      filled: fill.filled,
      total: fill.total,
      isNew: label === "New reference" && fill.filled === 0,
    };
  });
}

export function competitorSections(
  doc: GtmBrainDoc
): Array<{ id: CompetitorFieldId; label: string; body: string }> {
  return [
    { id: "name", label: "Name", body: doc.name || "" },
    { id: "domain", label: "Domain", body: doc.domain || "" },
    { id: "linkedinUrl", label: "LinkedIn URL", body: doc.linkedinUrl || "" },
    { id: "description", label: "Summary", body: doc.description || "" },
    { id: "notes", label: "Positioning notes", body: doc.notes || "" },
  ];
}
